// Utledning av arbeidsøkter fra commit-tidspunkt, og regnereglene for timer.
//
// HVORFOR DETTE ER EGEN, TESTET MODUL: timer er nær penger. En metode som
// overrapporterer er ikke en kosmetisk feil — den kan gi feil grunnlag for
// frivillighetsregnskap. Derfor er reglene eksplisitte, navngitte og testet, og
// alle anslag er merket som anslag.
//
// FORKASTET METODE (dokumentert her så den ikke gjeninnføres): «første til siste
// commit per dag». På vårt eget materiale ga den 174 timer der økt-metoden ga
// 93,5 — fordi en arbeidsøkt som krysser midnatt da leses som nesten et helt
// døgn. Ikke bruk den.
export const STANDARD_REGLER = {
    pauseMin: 90,
    oppstartMin: 20,
    maksOktMin: 8 * 60,
};
/**
 * Grupper commits i arbeidsøkter. Rekkefølge på inndata betyr ikke noe —
 * punktene sorteres. Duplikate sha-er slås sammen (samme commit kan komme fra
 * to grener).
 */
export function utledOkter(punkter, regler = STANDARD_REGLER) {
    const sett = new Map();
    for (const p of punkter)
        if (!sett.has(p.sha))
            sett.set(p.sha, p);
    const sortert = [...sett.values()].sort((a, b) => a.tsMs - b.tsMs);
    if (!sortert.length)
        return [];
    const pauseMs = Math.max(1, regler.pauseMin) * 60_000;
    const grupper = [];
    let cur = [sortert[0]];
    for (const p of sortert.slice(1)) {
        if (p.tsMs - cur[cur.length - 1].tsMs > pauseMs) {
            grupper.push(cur);
            cur = [p];
        }
        else
            cur.push(p);
    }
    grupper.push(cur);
    return grupper.map((g) => {
        const start = g[0].tsMs;
        const slutt = g[g.length - 1].tsMs;
        const spennMin = Math.round((slutt - start) / 60_000);
        const raa = spennMin + Math.max(0, regler.oppstartMin);
        const minutter = Math.min(raa, regler.maksOktMin);
        return {
            startTsMs: start,
            sluttTsMs: slutt,
            minutter,
            spennMin,
            kappet: raa > regler.maksOktMin,
            commits: g.map((x) => x.sha),
            forfattere: [...new Set(g.map((x) => x.forfatter).filter(Boolean))].sort(),
            emner: g.map((x) => x.emne ?? ''),
        };
    });
}
// ── Avrunding og visning ────────────────────────────────────────────────────
/**
 * Vis minutter som timer, avrundet til nærmeste halvtime. Avrunding skjer KUN
 * ved visning — lagret data er alltid hele minutter, slik at ingen presisjon
 * forsvinner i mellomlagring og summer ikke drifter.
 */
export function timerVist(minutter) {
    return Math.round((minutter / 60) * 2) / 2;
}
/** «7 t 30 min» — for menneskelesing. */
export function timerTekst(minutter) {
    const t = Math.floor(minutter / 60);
    const m = minutter % 60;
    if (!t)
        return `${m} min`;
    return m ? `${t} t ${m} min` : `${t} t`;
}
/**
 * Summér minutter og rund ÉN gang til slutt. Å runde hver rad først og så summere
 * gir drift: 12 økter à 20 min blir 12 × 0,5 t = 6 t i stedet for 4 t.
 */
export function sumTimer(minutter) {
    return timerVist(minutter.reduce((a, b) => a + b, 0));
}
/** Slutt-tidspunkt for et tidsrom. */
export function sluttAv(t) {
    return t.startTsMs + t.minutter * 60_000;
}
/** Overlapper to tidsrom i tid? Berøring i endepunktet regnes IKKE som overlapp. */
export function overlapper(a, b) {
    return a.startTsMs < sluttAv(b) && b.startTsMs < sluttAv(a);
}
/** Overlappet tid i minutter (0 hvis ingen). */
export function overlappMin(a, b) {
    const start = Math.max(a.startTsMs, b.startTsMs);
    const slutt = Math.min(sluttAv(a), sluttAv(b));
    return slutt > start ? Math.round((slutt - start) / 60_000) : 0;
}
/**
 * Finn alle overlapp i et sett tidsrom for SAMME person. Dette er hovedvernet mot
 * dobbeltføring: en manuelt meldt øvelse kl. 18-22 og en git-avledet økt kl.
 * 20-23 kan ikke begge være sanne. Funnene skal VISES til den som melder — de
 * skal ikke slettes automatisk, for verktøyet vet ikke hvilken som er riktig.
 */
export function finnOverlapp(rom) {
    const sortert = [...rom].sort((x, y) => x.startTsMs - y.startTsMs);
    const funn = [];
    for (let i = 0; i < sortert.length; i++) {
        for (let j = i + 1; j < sortert.length; j++) {
            // Sortert på start: når b starter etter at a er slutt, kan ingen senere overlappe a.
            if (sortert[j].startTsMs >= sluttAv(sortert[i]))
                break;
            const m = overlappMin(sortert[i], sortert[j]);
            if (m > 0)
                funn.push({ a: sortert[i], b: sortert[j], minutter: m });
        }
    }
    return funn;
}
/**
 * Summér uten å telle overlappende tid dobbelt: slå sammen tidsrom til
 * sammenhengende intervaller først. Brukes til «faktisk tid brukt», mens
 * rå sum brukes til «meldt tid». Avviket mellom de to er selve funnet.
 */
export function sammenslattMinutter(rom) {
    if (!rom.length)
        return 0;
    const sortert = [...rom].sort((a, b) => a.startTsMs - b.startTsMs);
    let sum = 0;
    let start = sortert[0].startTsMs;
    let slutt = sluttAv(sortert[0]);
    for (const r of sortert.slice(1)) {
        if (r.startTsMs > slutt) {
            sum += slutt - start;
            start = r.startTsMs;
            slutt = sluttAv(r);
        }
        else
            slutt = Math.max(slutt, sluttAv(r));
    }
    sum += slutt - start;
    return Math.round(sum / 60_000);
}
//# sourceMappingURL=okter.js.map